/** A participant step's fields — bookingmgmt `components/DynamicFields.js`, drawn from the
 *  preview model: one control per field type, a question by its option count, the
 *  two-column layout from `lg` (`wide`), email and phone hidden while "Minor" is ticked,
 *  and "* Required" once a required field is left empty. Typing works; nothing is sent. */
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { format as formatDate, isValid, parse as parseDate } from 'date-fns';

import { isQuestion, type PreCheckField } from '../../model';
import { GuestTextField, REQUIRED_CAPTION_SX, REQUIRED_OUTLINE_SX } from '../guestTheme';
import { GUEST } from '../guestTokens';
import {
  categoryFor,
  fieldControl,
  fieldLabel,
  fieldSections,
  isHiddenForMinor,
  questionOf,
  type GuestPreviewOptions,
  type GuestPreviewTag,
} from '../previewModel';

export type FieldValues = Readonly<Record<string, unknown>>;

export interface GuestFieldsProps {
  fields: readonly PreCheckField[];
  values: FieldValues;
  onChange: (id: string, value: unknown) => void;
  touched: Readonly<Record<string, boolean>>;
  onBlur: (id: string) => void;
  options: GuestPreviewOptions;
  /** The guest layout from its `lg` breakpoint: two columns. */
  wide: boolean;
}

const groupLabelSx = {
  display: 'block',
  m: 0,
  p: 0,
  mb: 0.25,
  textAlign: 'left',
  color: GUEST.label,
  fontSize: '13px',
  lineHeight: '16px',
  fontWeight: 400,
} as const;

const optionLabelSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  maxWidth: '100%',
  cursor: 'pointer',
  fontSize: '16px',
  lineHeight: '24px',
  color: '#000000',
} as const;

const controlLabelSx = {
  width: '100%',
  mr: 0,
  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0, overflow: 'hidden' },
} as const;

const selectSx = {
  minWidth: 0,
  '& .MuiSelect-select': {
    minHeight: '28px !important',
    padding: '12px 32px 0 0 !important',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiSelect-icon': { right: '12px' },
} as const;

const tagLabel = (t: GuestPreviewTag) => t.friendly_name || t.name;

function isEmpty(field: PreCheckField, value: unknown): boolean {
  if (isQuestion(field)) return !Array.isArray(value) || value.length === 0;
  if (field.type === 'boolean') return !value;
  return value === undefined || value === null || String(value).trim() === '';
}

function toPickerDate(v: unknown): Date | null {
  if (typeof v !== 'string' || v === '') return null;
  const d = parseDate(v, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
}

function GuestField({
  field,
  value,
  touched,
  onChange,
  onBlur,
  options,
}: {
  field: PreCheckField;
  value: unknown;
  touched: boolean;
  onChange: (id: string, value: unknown) => void;
  onBlur: (id: string) => void;
  options: GuestPreviewOptions;
}) {
  const category = isQuestion(field) ? categoryFor(field, options) : undefined;
  const control = fieldControl(field, category);
  const question = isQuestion(field) ? questionOf(field, category) : null;
  const required = question ? question.required : field.required === true;
  const showRequired = required && touched && isEmpty(field, value);
  const requiredCaption = showRequired ? (
    <Typography variant="caption" sx={REQUIRED_CAPTION_SX}>
      {field.id === 'email' ? '* Email cannot be blank' : '* Required'}
    </Typography>
  ) : null;
  const outline = showRequired ? REQUIRED_OUTLINE_SX : {};
  const blur = () => onBlur(field.id);
  const text = typeof value === 'string' || typeof value === 'number' ? value : '';

  if (question) {
    const picked = Array.isArray(value) ? (value as number[]) : [];
    const toggle = (id: number, on: boolean) => onChange(field.id, on ? [...picked, id] : picked.filter((x) => x !== id));
    const bordered = control === 'radio' || control === 'checkboxes';
    const legend = (
      <FormLabel component="legend" sx={groupLabelSx}>
        {question.label}
        {required ? ' *' : ''}
      </FormLabel>
    );
    return (
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Box
          sx={{
            minWidth: 0,
            width: '100%',
            ...(bordered
              ? { border: `1px solid ${GUEST.inputBorder}`, borderRadius: `${GUEST.radius}px`, bgcolor: '#FFFFFF', px: 2, pt: 1, pb: 1.5 }
              : {}),
            ...(bordered && showRequired ? { borderColor: GUEST.error } : {}),
          }}
        >
          {control === 'checkboxes' && (
            <FormGroup onBlur={blur}>
              {legend}
              {question.tags.map((t) => (
                <FormControlLabel
                  key={t.id}
                  control={<Checkbox checked={picked.includes(t.id)} onChange={(e) => toggle(t.id, e.target.checked)} sx={{ py: 0.5 }} />}
                  label={
                    <Typography title={tagLabel(t)} sx={optionLabelSx}>
                      {tagLabel(t)}
                    </Typography>
                  }
                  sx={controlLabelSx}
                />
              ))}
            </FormGroup>
          )}
          {control === 'radio' && (
            <RadioGroup value={picked[0] ?? ''} onChange={(e) => onChange(field.id, [Number(e.target.value)])} onBlur={blur} name={field.id}>
              {legend}
              {question.tags.map((t) => (
                <FormControlLabel
                  key={t.id}
                  value={t.id}
                  control={<Radio sx={{ py: 0.5 }} />}
                  label={
                    <Typography title={tagLabel(t)} sx={optionLabelSx}>
                      {tagLabel(t)}
                    </Typography>
                  }
                  sx={controlLabelSx}
                />
              ))}
            </RadioGroup>
          )}
          {control === 'select' && (
            <GuestTextField
              fullWidth
              select
              label={question.label}
              value={picked[0] ?? ''}
              onChange={(e) => onChange(field.id, [Number(e.target.value)])}
              onBlur={blur}
              sx={{ ...outline, ...selectSx }}
            >
              {question.tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {tagLabel(t)}
                </MenuItem>
              ))}
            </GuestTextField>
          )}
          {control === 'multiselect' && (
            <GuestTextField
              fullWidth
              select
              label={question.label}
              value={picked}
              onChange={(e) => onChange(field.id, (e.target.value as unknown as number[]).map(Number))}
              onBlur={blur}
              sx={{ ...outline, ...selectSx }}
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (sel) => {
                    const labels = question.tags.filter((t) => (sel as number[]).includes(t.id)).map(tagLabel);
                    return labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
                  },
                },
              }}
            >
              {question.tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Checkbox checked={picked.includes(t.id)} />
                  <ListItemText primary={tagLabel(t)} />
                </MenuItem>
              ))}
            </GuestTextField>
          )}
        </Box>
        {requiredCaption}
      </Box>
    );
  }

  if (control === 'checkbox') {
    return (
      <Box sx={showRequired ? { border: `1px solid ${GUEST.error}`, borderRadius: `${GUEST.radius}px`, px: 1 } : {}}>
        <FormControlLabel
          control={<Checkbox checked={Boolean(value)} onChange={(e) => onChange(field.id, e.target.checked)} onBlur={blur} name={field.id} />}
          label={fieldLabel(field)}
          sx={{ width: '100%' }}
        />
        {requiredCaption}
      </Box>
    );
  }

  if (control === 'date') {
    return (
      <Box>
        <DatePicker
          label={field.name}
          value={toPickerDate(value)}
          onChange={(d) => onChange(field.id, d && isValid(d) ? formatDate(d, 'yyyy-MM-dd') : '')}
          format="yyyy-MM-dd"
          reduceAnimations
          slots={{ textField: GuestTextField }}
          slotProps={{ textField: { fullWidth: true, autoComplete: 'off', placeholder: '2000-01-01', sx: outline, onBlur: blur } }}
        />
        {requiredCaption}
      </Box>
    );
  }

  return (
    <Box>
      <GuestTextField
        fullWidth
        name={field.id}
        label={field.name}
        value={text}
        onChange={(e) => onChange(field.id, e.target.value)}
        onBlur={blur}
        type={control === 'number' ? 'number' : control === 'email' ? 'email' : 'text'}
        autoComplete="off"
        multiline={control === 'multiline'}
        {...(control === 'multiline' ? { rows: 3 } : {})}
        sx={outline}
      />
      {requiredCaption}
    </Box>
  );
}

export function GuestFields({ fields, values, onChange, touched, onBlur, options, wide }: GuestFieldsProps) {
  const minor = values.is_minor === true;
  const visible = fields.filter((f) => !(minor && isHiddenForMinor(f)));
  const one = (f: PreCheckField) => (
    <Box key={f.id} data-guest-field={f.id} data-guest-control={fieldControl(f, isQuestion(f) ? categoryFor(f, options) : undefined)} sx={{ minWidth: 0 }}>
      <GuestField field={f} value={values[f.id]} touched={touched[f.id] === true} onChange={onChange} onBlur={onBlur} options={options} />
    </Box>
  );
  if (!wide) {
    return (
      <Box data-guest-layout="single">
        {visible.map((f) => (
          <Box key={f.id} sx={{ mb: 3 }}>
            {one(f)}
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box data-guest-layout="columns" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {fieldSections(visible).map((s) =>
        s.kind === 'full' ? (
          <Box key={`full-${s.field.id}`} data-guest-section="full">
            {one(s.field)}
          </Box>
        ) : (
          <Box key={`cols-${s.left[0]!.id}`} data-guest-section="columns" sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
            {[s.left, s.right].map((col, c) => (
              <Box key={c} data-guest-column={c === 0 ? 'left' : 'right'} sx={{ flex: 1, minWidth: 0 }}>
                {col.map((f, i) => (
                  <Box key={f.id} sx={{ mb: i === col.length - 1 ? 0 : 3, minWidth: 0 }}>
                    {one(f)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ),
      )}
    </Box>
  );
}
